import _ from 'underscore';
import { NULL_TRACK, NULL_PROGRAM, Track } from '@/model/track';
import trackDao from '@/dao/track_dao';
import { format } from 'date-fns';

export const TrackStore = {
  namespaced: true,

  state: {
    tracks: [],
    programs: [],
    program1s: [],
    templateTrack: NULL_TRACK
  },

  getters: {
    getTemplateTrack: (state) => state.templateTrack,
    getTracks: (state) => state.tracks,
    getTrackById: (state) => (id) => _(state.tracks).find((t) => t.id === id),

    getSessionById: (state) => (sessionId) => {
      //Flatten tracks
      const sessions = _(state.tracks).reduce((memo, track) => {
        for (let key of Track.programKeys()) {
          memo.push(track[key]);
        }
        return memo;
      }, []);

      const session = _(sessions).findWhere({ sessionId });

      if (_(session).isUndefined()) {
        console.log('No session found for ID', sessionId);
        return null;
      } else {
        return session;
      }
    },

    //Get mentor session for trainee session and vice versa
    getAssociatedSession: (state) => (session) => {
      if (!(session || {}).trackId) {
        return null;
      }

      const findIdx = session.idx;
      const findMentorSession = !session.mentorSession;
      if (findMentorSession && findIdx % 2 === 0) {
        console.log('No mentor session available for idx', findIdx);
      }
      const track = _.findWhere(state.tracks, { id: session.trackId });
      const key = sprintf('%srogram%d', findMentorSession ? 'mentorP' : 'p', findIdx);
      return track[key];
    },

    getPrograms: (state) => state.programs,
    getProgram1s: (state) => state.program1s,
    getProgramByIndex: (state) => (idx) => _(state.programs).find((p) => p.name.endsWith(idx + '')) || NULL_PROGRAM,
    getProgramsByYear: (state) => (year) => {
      const validTracks = _(state.tracks).filter((track) => !track.canceled);

      const vts = [1, 2, 3, 4].map((i) => _(validTracks).pluck(`program${i}`)).flat();

      return _(vts)
        .chain()
        .filter((program) => _(program.dates).any((d) => d.getFullYear() === year))
        .sortBy((program) => program.dates[0])
        .value();
    },

    // Not used?
    // getProgram1sByYear: (state) => (year) => {
    //   const program1s = _(state.tracks).chain()
    //     .filter((track) => !track.canceled && !track.hasCanceledSessions
    //     )
    //     .map((track) => {
    //       return track.program1;
    //     })
    //     .filter((program) => {
    //       return _.any(program.dates, (d) => d.getFullYear() === year);
    //     })
    //     .sortBy((program) => program.dates[0])
    //     .value();
    //   return program1s;
    // },

    //Includes past dates (for reporting)
    getProgramsNoYear: (state) => {
      const vts = [1, 2, 3, 4].map((i) => _(state.tracks).pluck(`program${i}`)).flat();
      const programs = _(vts)
        .chain()
        .sortBy((program) => program.dates[0])
        .value();
      return programs;
    },

    getProgram1sNoYear: (state) => {
      //This getter does not include filtering for non-admin users, only used for admin "Applications" route
      return _.chain(state.tracks)
        .filter((track) => !track.canceled)
        .pluck('program1')
        .filter((session) => !!session.sessionId && 1 === session.idx && !session.canceled)
        .sortBy((program) => program.dates[0])
        .value();
    },

    //Includes past dates (for reporting)
    getMentorProgramsNoYear: (state) => _([1, 3].map((i) => _(state.tracks).pluck(`mentorProgram${i}`)).flat()).sortBy((program) => program.dates[0])
  },

  mutations: {
    setTemplateTrack: (state, templateTrack) => (
      state.templateTrack = templateTrack
    ),
    setTracks: (state, tracks) => (
      state.tracks = tracks
    ),
    setPrograms: (state, programs) => (
      state.programs = programs
    ),
    setProgram1s: (state, program1s) => (
      state.program1s = program1s
    ),

    rollback: (state, trackId) => {
      const track = _(state.tracks).find((t) => t.id === trackId);

      if (!!track) {
        track.rollback();
        track.disableEditMode();
      }
    },

    addTrack: (state, track) => {
      if (null === track) {
        return;
      }

      const index = _(state.tracks).findIndex((t) => t.id === track.id);

      //Remove track if it already exists
      if (index >= 0) {
        state.tracks.splice(index, 1);
      }

      state.tracks.push(track);
    },

    removeTrack: (state, track) => {
      if (null === track) {
        return;
      }

      const index = _(state.tracks).findIndex((t) => _(t.id).isEqual(track.id))

      //Remove track if it already exists
      if (index >= 0) {
        state.tracks.splice(index, 1);
      }
    }
  },

  actions: {
    saveTrack: async ({ commit }, track) => {
      const updatedTrack = await trackDao.saveTrack(track.workspace);
      updatedTrack.commit();
      commit('addTrack', updatedTrack);
    },

    cancelTrack: async ({ commit }, track) => {
      await trackDao.cancelTrack(track);
      commit('addTrack', track); //Re-add cancelled track
    },

    deleteTrack: async ({ commit }, track) => {
      await trackDao.deleteTrack(track);
      commit('removeTrack', track);
    },

    loadTracks: async (context) => {
      //Used for reactivity, do not cache
      const tracks = await trackDao.getTracks();
      context.commit('setTracks', tracks);
    },

    //Loads program details (beyond static model)
    loadPrograms: async ({ commit }) => {
      const programs = await trackDao.getPrograms();
      commit('setPrograms', programs);
    },

    //Loads program 1 sessions with limited model (ID, label)
    loadProgram1s: async ({ commit }) => {
      const now = format(new Date(), 'yyyy-MM-dd');
      const program1s = await trackDao.getProgramOnes('session', 'false', now, 'ASC');
      commit('setProgram1s', program1s);
    },

    loadTemplateTrack: async (context) => {
      //Only need to load once
      if (_(context.state.templateTrack).isEqual(NULL_TRACK)) {
        const program1 = await trackDao.getSessionTemplate(1, false);
        const program2 = await trackDao.getSessionTemplate(2, false);
        const program3 = await trackDao.getSessionTemplate(3, false);
        const program4 = await trackDao.getSessionTemplate(4, false);
        const mentorProgram1 = await trackDao.getSessionTemplate(1, true);
        const mentorProgram3 = await trackDao.getSessionTemplate(3, true);
        const templateTrack = Track.create({
          id: 0,
          program1: program1,
          program2: program2,
          program3: program3,
          program4: program4,
          mentorProgram1: mentorProgram1,
          mentorProgram3: mentorProgram3,
          workspace: true
        });

        context.commit('setTemplateTrack', templateTrack);
      }
    },

    loadTrack: async ({ commit }, trackId) => {
      const track = await trackDao.getTrackById(trackId);
      commit('addTrack', track);
    }
  }
};

export default TrackStore;
